import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

const LoginHistoryDialog = ({ open, onClose, userEmail }) => {
  const { currentUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && userEmail) {
      fetchLoginHistory();
    }
  }, [open, userEmail]);

  const fetchLoginHistory = async () => {
    setLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${apiService.baseURL}/api/allowed-users/login-history/${encodeURIComponent(userEmail)}?limit=50`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHistory(data.history);
      } else {
        toast.error('로그인 이력 조회에 실패했습니다');
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
      toast.error('로그인 이력 조회 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">로그인 이력</Typography>
          <Typography variant="body2" color="text.secondary">
            {userEmail}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>로그인 시간</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>IP 주소</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>브라우저</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {new Date(item.login_at).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={item.success ? <SuccessIcon /> : <ErrorIcon />}
                        label={item.success ? '성공' : '실패'}
                        color={item.success ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.ip_address || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>
                      {item.user_agent
                        ? item.user_agent.length > 60
                          ? `${item.user_agent.substring(0, 60)}...`
                          : item.user_agent
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && history.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">로그인 이력이 없습니다</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginHistoryDialog;
